<template>
  <v-dialog :model-value="modelValue" max-width="560" @update:model-value="close">
    <v-card class="cat-dialog">
      <SnackbarComp :SnackbarComponent="snackbar" />

      <v-card-title class="cat-dialog__head">
        <div class="cat-dialog__icon">
          <v-icon icon="mdi-puzzle-outline" size="22" />
        </div>
        <div>
          <div class="cat-dialog__title">{{ template?.template_name || record?.action_name || 'Configure action' }}</div>
          <div class="cat-dialog__subtitle">
            {{ record?.is_active ? 'Active' : 'Not active yet -- add all credentials below to enable it' }}
          </div>
        </div>
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" density="comfortable" @click="close" />
      </v-card-title>

      <v-card-text>
        <p v-if="template?.template_description" class="cat-dialog__desc">
          {{ template.template_description }}
        </p>

        <div v-if="loading" class="cat-dialog__loading">
          <v-progress-circular indeterminate color="primary" size="28" />
        </div>

        <template v-else>
          <div class="cat-dialog__section-title">Credentials</div>
          <p v-if="(record?.configured_credential_keys || []).length" class="cat-dialog__hint">
            Dots mean a field is already saved -- leave it as-is to keep it, or type to replace it.
          </p>
          <div
            v-for="field in template?.credential_fields || []"
            :key="field.key"
            class="cat-dialog__field"
          >
            <label class="cat-dialog__label">{{ field.label }}</label>
            <v-text-field
              v-model="credentials[field.key]"
              :type="field.type === 'secret' ? 'password' : 'text'"
              variant="outlined"
              density="comfortable"
              hide-details="auto"
              :placeholder="isConfigured(field.key) ? '••••••••••••' : ''"
            />
          </div>

          <div class="cat-dialog__section-title cat-dialog__section-title--spaced">Notifications</div>
          <p class="cat-dialog__hint">
            The message content is fixed by Yandle -- you can only turn each channel on/off and choose who receives it.
          </p>

          <div class="cat-dialog__channel">
            <div class="cat-dialog__channel-head">
              <span>Email</span>
              <v-switch v-model="notifications.email.enabled" color="primary" density="compact" hide-details />
            </div>
            <v-combobox
              v-if="notifications.email.enabled"
              v-model="notifications.email.send_to"
              multiple
              chips
              closable-chips
              variant="outlined"
              density="comfortable"
              hide-details="auto"
              placeholder="Add email address and press enter"
            />
          </div>

          <div class="cat-dialog__channel">
            <div class="cat-dialog__channel-head">
              <span>WhatsApp</span>
              <v-switch v-model="notifications.whatsapp.enabled" color="primary" density="compact" hide-details />
            </div>
            <v-combobox
              v-if="notifications.whatsapp.enabled"
              v-model="notifications.whatsapp.send_to"
              multiple
              chips
              closable-chips
              variant="outlined"
              density="comfortable"
              hide-details="auto"
              placeholder="Add phone number (e.g. +919876543210) and press enter"
            />
          </div>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="close">Cancel</v-btn>
        <v-btn variant="flat" color="primary" :loading="saving" :disabled="loading" @click="save">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch } from 'vue'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import { fetchActionTemplate, configureTemplateActionApi } from '@/api/onCallActionsApi'
import { normalizeSendToList } from '@/utils/onCallNotifications'
import { isApiSuccess, getApiMessage } from '@/mixins/Extras/ApiResponse'

defineOptions({ name: 'ConfigureActionCredentialsDialog' })

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  record: { type: Object, default: null },
})

const emit = defineEmits(['update:modelValue', 'saved', 'error'])

const loading = ref(false)
const saving = ref(false)
const template = ref(null)
const credentials = ref({})
const notifications = ref({
  email: { enabled: false, send_to: [] },
  whatsapp: { enabled: false, send_to: [] },
})
const snackbar = ref({})

function isConfigured(key) {
  return (props.record?.configured_credential_keys || []).includes(key)
}

// normalizeNotificationChannel() hands us `send_to` already joined into a
// comma-separated string and keeps the array as `send_to_list`. Spreading the
// string would give the combobox one chip per character ("+919449373014" ->
// 13 chips), and saving would persist those characters as the recipients.
function recipientList(channel) {
  return normalizeSendToList(channel?.send_to_list ?? channel?.send_to)
}

function resetForm() {
  template.value = null
  credentials.value = {}
  notifications.value = {
    email: {
      enabled: Boolean(props.record?.notifications?.email?.enabled),
      send_to: recipientList(props.record?.notifications?.email),
    },
    whatsapp: {
      enabled: Boolean(props.record?.notifications?.whatsapp?.enabled),
      send_to: recipientList(props.record?.notifications?.whatsapp),
    },
  }
}

async function loadTemplate() {
  const templateId = props.record?.template_id
  if (!templateId) return
  loading.value = true
  try {
    const response = await fetchActionTemplate({ template_id: templateId })
    if (isApiSuccess(response)) {
      template.value = response.data
    } else {
      emit('error', getApiMessage(response, 'Failed to load action template'))
    }
  } catch (err) {
    emit('error', err)
  } finally {
    loading.value = false
  }
}

watch(
  () => props.modelValue,
  (open) => {
    if (!open) return
    resetForm()
    void loadTemplate()
  },
)

function close() {
  emit('update:modelValue', false)
}

async function save() {
  saving.value = true
  try {
    const enteredCredentials = Object.fromEntries(
      Object.entries(credentials.value).filter(([, v]) => String(v || '').trim() !== ''),
    )
    // credentials/notifications are AWSJSON on the wire -- GraphQL rejects
    // an object literal for a scalar that's structurally a String, so both
    // must be sent as JSON-encoded strings, not nested objects.
    const payload = {
      action_id: props.record?.action_id,
      ...(Object.keys(enteredCredentials).length > 0 ? { credentials: JSON.stringify(enteredCredentials) } : {}),
      notifications: JSON.stringify({
        email: {
          enabled: notifications.value.email.enabled,
          send_to: normalizeSendToList(notifications.value.email.send_to),
        },
        whatsapp: {
          enabled: notifications.value.whatsapp.enabled,
          send_to: normalizeSendToList(notifications.value.whatsapp.send_to),
        },
      }),
    }
    const response = await configureTemplateActionApi(payload)
    if (!isApiSuccess(response)) {
      emit('error', getApiMessage(response, 'Failed to save configuration'))
      return
    }
    emit('saved', response)
    close()
  } catch (err) {
    emit('error', err)
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.cat-dialog__head {
  display: flex;
  align-items: center;
  gap: 12px;
}
.cat-dialog__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(99, 102, 241, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6366f1;
  flex-shrink: 0;
}
.cat-dialog__title {
  font-weight: 650;
  font-size: 1rem;
}
.cat-dialog__subtitle {
  font-size: 0.78rem;
  color: #64748b;
}
.cat-dialog__desc {
  font-size: 0.85rem;
  color: #475569;
  margin-bottom: 16px;
}
.cat-dialog__loading {
  display: flex;
  justify-content: center;
  padding: 24px 0;
}
.cat-dialog__section-title {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #64748b;
  margin-bottom: 10px;
}
.cat-dialog__section-title--spaced {
  margin-top: 24px;
}
.cat-dialog__hint {
  font-size: 0.78rem;
  color: #94a3b8;
  margin-bottom: 12px;
}
.cat-dialog__field {
  margin-bottom: 12px;
}
.cat-dialog__label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 4px;
  color: #334155;
}
.cat-dialog__channel {
  margin-bottom: 16px;
}
.cat-dialog__channel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  font-size: 0.85rem;
  margin-bottom: 8px;
}
</style>
